import {useEffect, useMemo, useState} from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {MoneyField} from '@/components/common';

export interface DepreciationScheduleRow {
    item: string;
    tenPool: number; // 10%
    twentyPool: number; // 20%
    thirtyPool: number; // 30%
    hpThirtyPool: number; // HP 30%
    balancingCharge?: number;
    balancingAllowance?: number;
    allowance?: number;
}

export interface BfPools {
    tenPool: number;
    twentyPool: number;
    thirtyPool: number;
    hpThirtyPool: number;
}

interface DepreciationScheduleTableProps {
    rows: DepreciationScheduleRow[];
    currency: string | null;
    wdvBfPools?: BfPools;
    onWdvBfPoolsChange?: (next: BfPools) => void;
    /** MULTI YOA: only first YOA tab may edit W.D.V. b/f (default true). */
    wdvBfEditable?: boolean;
    baPools?: BfPools;
    onBaPoolsChange?: (next: BfPools) => void;
    /** Backend-derived Less: Sales proceed pools (read-only; no user edit). */
    salesProceedPools?: BfPools;
}

export function DepreciationScheduleTable({
                                              rows, currency,
                                              wdvBfPools, onWdvBfPoolsChange,
                                              wdvBfEditable = true,
                                              baPools, onBaPoolsChange,
                                              salesProceedPools,
                                          }: DepreciationScheduleTableProps) {
    const safeRows = rows || [];

    const wdvRow = useMemo(() => {
        return safeRows.find((r) => (r?.item || '').trim() === 'W.D.V. b/f') || null;
    }, [safeRows]);

    const baRow = useMemo(() => {
        return safeRows.find((r) => (r?.item || '').trim() === 'Balancing allowance') || null;
    }, [safeRows]);

    const salesProceedRow = useMemo(() => {
        return safeRows.find((r) => (r?.item || '').trim() === 'Less: Sales proceed') || null;
    }, [safeRows]);

    const additionsRow = useMemo(() => {
        return safeRows.find((r) => (r?.item || '').trim() === 'Additions') || null;
    }, [safeRows]);

    const ia60Row = useMemo(() => {
        return safeRows.find((r) => (r?.item || '').trim() === 'I.A. - 60%') || null;
    }, [safeRows]);

    const [wdvPoolsState, setWdvPoolsState] = useState<BfPools>({
        tenPool: 0,
        twentyPool: 0,
        thirtyPool: 0,
        hpThirtyPool: 0,
    });

    const [baPoolsState, setBaPoolsState] = useState<BfPools>({
        tenPool: 0,
        twentyPool: 0,
        thirtyPool: 0,
        hpThirtyPool: 0,
    });

    const [salesProceedPoolsState, setSalesProceedPoolsState] = useState<BfPools>({
        tenPool: 0,
        twentyPool: 0,
        thirtyPool: 0,
        hpThirtyPool: 0,
    });

    // 1.1 计算 total 行 (W.D.V. b/f + Additions + I.A. - 60% + Less: Sales proceed)
    const totalRowPools = useMemo<BfPools>(() => {
        const additionsTenPool = Number(additionsRow?.tenPool) || 0;
        const additionsTwentyPool = Number(additionsRow?.twentyPool) || 0;
        const additionsThirtyPool = Number(additionsRow?.thirtyPool) || 0;
        const additionsHpThirtyPool = Number(additionsRow?.hpThirtyPool) || 0;

        const ia60TenPool = Number(ia60Row?.tenPool) || 0;
        const ia60TwentyPool = Number(ia60Row?.twentyPool) || 0;
        const ia60ThirtyPool = Number(ia60Row?.thirtyPool) || 0;
        const ia60HpThirtyPool = Number(ia60Row?.hpThirtyPool) || 0;

        return {
            tenPool: wdvPoolsState.tenPool + additionsTenPool + ia60TenPool + salesProceedPoolsState.tenPool,
            twentyPool: wdvPoolsState.twentyPool + additionsTwentyPool + ia60TwentyPool + salesProceedPoolsState.twentyPool,
            thirtyPool: wdvPoolsState.thirtyPool + additionsThirtyPool + ia60ThirtyPool + salesProceedPoolsState.thirtyPool,
            hpThirtyPool: wdvPoolsState.hpThirtyPool + additionsHpThirtyPool + ia60HpThirtyPool + salesProceedPoolsState.hpThirtyPool,
        };
    }, [wdvPoolsState, salesProceedPoolsState, additionsRow, ia60Row]);

    // 1.2 计算 A.A. 行（各 pool 先四舍五入，allowance 为取整后之和）
    const roundHalfUp = (value: number) => {
        if (!Number.isFinite(value) || value === 0) return 0;
        return value >= 0 ? Math.round(value) : -Math.round(Math.abs(value));
    };

    const aaRowPools = useMemo<BfPools>(() => {
        const raw = {
            tenPool: totalRowPools.tenPool < 0 ? 0 : -(totalRowPools.tenPool * 0.1),
            twentyPool: totalRowPools.twentyPool < 0 ? 0 : -(totalRowPools.twentyPool * 0.2),
            thirtyPool: totalRowPools.thirtyPool < 0 ? 0 : -(totalRowPools.thirtyPool * 0.3),
            hpThirtyPool: totalRowPools.hpThirtyPool < 0 ? 0 : -(totalRowPools.hpThirtyPool * 0.3),
        };
        return {
            tenPool: roundHalfUp(raw.tenPool),
            twentyPool: roundHalfUp(raw.twentyPool),
            thirtyPool: roundHalfUp(raw.thirtyPool),
            hpThirtyPool: roundHalfUp(raw.hpThirtyPool),
        };
    }, [totalRowPools]);

    // 1.3 计算 Balancing charge 行
    const balancingChargeRowPools = useMemo(() => {
        const tenPool = totalRowPools.tenPool <= 0 ? Math.abs(totalRowPools.tenPool) : 0;
        const twentyPool = totalRowPools.twentyPool <= 0 ? Math.abs(totalRowPools.twentyPool) : 0;
        const thirtyPool = totalRowPools.thirtyPool <= 0 ? Math.abs(totalRowPools.thirtyPool) : 0;
        const hpThirtyPool = totalRowPools.hpThirtyPool <= 0 ? Math.abs(totalRowPools.hpThirtyPool) : 0;
        const balancingChargeValue = -(tenPool + twentyPool + thirtyPool + hpThirtyPool);
        return {
            tenPool,
            twentyPool,
            thirtyPool,
            hpThirtyPool,
            balancingCharge: balancingChargeValue,
        };
    }, [totalRowPools]);

    // 1.4 计算 Balancing allowance 行的 balancingAllowance 列值
    const balancingAllowanceValue = useMemo(() => {
        return -(baPoolsState.tenPool + baPoolsState.twentyPool + baPoolsState.thirtyPool + baPoolsState.hpThirtyPool);
    }, [baPoolsState]);

    // 1.5 计算 W.D.V. c/f 行
    const wdvCfRowPools = useMemo<BfPools>(() => {
        return {
            tenPool: totalRowPools.tenPool + aaRowPools.tenPool + balancingChargeRowPools.tenPool + baPoolsState.tenPool,
            twentyPool: totalRowPools.twentyPool + aaRowPools.twentyPool + balancingChargeRowPools.twentyPool + baPoolsState.twentyPool,
            thirtyPool: totalRowPools.thirtyPool + aaRowPools.thirtyPool + balancingChargeRowPools.thirtyPool + baPoolsState.thirtyPool,
            hpThirtyPool: totalRowPools.hpThirtyPool + aaRowPools.hpThirtyPool + balancingChargeRowPools.hpThirtyPool + baPoolsState.hpThirtyPool,
        };
    }, [totalRowPools, aaRowPools, balancingChargeRowPools, baPoolsState]);

    // 1.6 计算最后一行的汇总值
    const lastRowSummary = useMemo(() => {
        return {
            balancingCharge: balancingChargeRowPools.balancingCharge,
            balancingAllowance: Math.abs(balancingAllowanceValue),
        };
    }, [balancingChargeRowPools.balancingCharge, balancingAllowanceValue]);

    // 找到各行的索引
    const rowIndices = useMemo(() => {
        let salesProceedIdx = -1;
        let totalIdx = -1;
        let aaIdx = -1;
        let balancingChargeIdx = -1;
        let balancingAllowanceIdx = -1;
        let wdvCfIdx = -1;
        let lastBlankIdx = -1;

        safeRows.forEach((r, idx) => {
            const itemKey = (r?.item || '').trim();
            if (itemKey === 'Less: Sales proceed') {
                salesProceedIdx = idx;
                totalIdx = idx + 1;
            } else if (itemKey === 'A.A.') {
                aaIdx = idx;
            } else if (itemKey === 'Balancing charge') {
                balancingChargeIdx = idx;
            } else if (itemKey === 'Balancing allowance') {
                balancingAllowanceIdx = idx;
            } else if (itemKey === 'W.D.V. c/f') {
                wdvCfIdx = idx;
                lastBlankIdx = idx + 1;
            }
        });

        return {salesProceedIdx, totalIdx, aaIdx, balancingChargeIdx, balancingAllowanceIdx, wdvCfIdx, lastBlankIdx};
    }, [safeRows]);

    useEffect(() => {
        if (wdvBfPools) {
            setWdvPoolsState({
                tenPool: Number(wdvBfPools.tenPool) || 0,
                twentyPool: Number(wdvBfPools.twentyPool) || 0,
                thirtyPool: Number(wdvBfPools.thirtyPool) || 0,
                hpThirtyPool: Number(wdvBfPools.hpThirtyPool) || 0,
            });
            return;
        }
        if (wdvRow) {
            setWdvPoolsState({
                tenPool: Number(wdvRow.tenPool) || 0,
                twentyPool: Number(wdvRow.twentyPool) || 0,
                thirtyPool: Number(wdvRow.thirtyPool) || 0,
                hpThirtyPool: Number(wdvRow.hpThirtyPool) || 0,
            });
        }
    }, [wdvBfPools, wdvRow]);

    useEffect(() => {
        if (baPools) {
            setBaPoolsState({
                tenPool: Number(baPools.tenPool) || 0,
                twentyPool: Number(baPools.twentyPool) || 0,
                thirtyPool: Number(baPools.thirtyPool) || 0,
                hpThirtyPool: Number(baPools.hpThirtyPool) || 0,
            });
            return;
        }
        if (baRow) {
            setBaPoolsState({
                tenPool: Number(baRow.tenPool) || 0,
                twentyPool: Number(baRow.twentyPool) || 0,
                thirtyPool: Number(baRow.thirtyPool) || 0,
                hpThirtyPool: Number(baRow.hpThirtyPool) || 0,
            });
        }
    }, [baPools, baRow]);

    // Less: Sales proceed — prefer balancingList row (backend), else parent hydrate from same source
    useEffect(() => {
        const source = salesProceedRow ?? (salesProceedPools
            ? {
                tenPool: salesProceedPools.tenPool,
                twentyPool: salesProceedPools.twentyPool,
                thirtyPool: salesProceedPools.thirtyPool,
                hpThirtyPool: salesProceedPools.hpThirtyPool,
            }
            : null);
        if (!source) return;
        setSalesProceedPoolsState({
            tenPool: Number(source.tenPool) || 0,
            twentyPool: Number(source.twentyPool) || 0,
            thirtyPool: Number(source.thirtyPool) || 0,
            hpThirtyPool: Number(source.hpThirtyPool) || 0,
        });
    }, [salesProceedRow, salesProceedPools]);

    // poolsRow: 1 = W.D.V. b/f, 3 = Balancing allowance (Sales proceed is backend-only)
    const updatePools = (partial: Partial<BfPools>, poolsRow: number = 1) => {
        if (poolsRow === 3) {
            setBaPoolsState((prev) => {
                const next: BfPools = {
                    tenPool: Number(partial.tenPool ?? prev.tenPool) || 0,
                    twentyPool: Number(partial.twentyPool ?? prev.twentyPool) || 0,
                    thirtyPool: Number(partial.thirtyPool ?? prev.thirtyPool) || 0,
                    hpThirtyPool: Number(partial.hpThirtyPool ?? prev.hpThirtyPool) || 0,
                };
                onBaPoolsChange?.(next);
                return next;
            });
        } else {
            setWdvPoolsState((prev) => {
                const next: BfPools = {
                    tenPool: Number(partial.tenPool ?? prev.tenPool) || 0,
                    twentyPool: Number(partial.twentyPool ?? prev.twentyPool) || 0,
                    thirtyPool: Number(partial.thirtyPool ?? prev.thirtyPool) || 0,
                    hpThirtyPool: Number(partial.hpThirtyPool ?? prev.hpThirtyPool) || 0,
                };
                onWdvBfPoolsChange?.(next);
                return next;
            });
        }
    };

    if (safeRows.length === 0) return null;

    return (
        <Box>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell colSpan={8} align="center" sx={{py: 1}}>
                            <Typography variant="h6" sx={{fontWeight: 600}}>
                                DEPRECIATION ALLOWANCE SCHEDULE
                            </Typography>
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{fontWeight: 'bold'}}/>
                        <TableCell sx={{fontWeight: 'bold'}} align="center">10% pool</TableCell>
                        <TableCell sx={{fontWeight: 'bold'}} align="center">20% pool</TableCell>
                        <TableCell sx={{fontWeight: 'bold'}} align="center">30% pool</TableCell>
                        <TableCell sx={{fontWeight: 'bold'}} align="center">HP 30% pool</TableCell>
                        <TableCell sx={{fontWeight: 'bold'}} align="center">Balancing charge</TableCell>
                        <TableCell sx={{fontWeight: 'bold'}} align="center">Balancing allowance</TableCell>
                        <TableCell sx={{fontWeight: 'bold'}} align="center">Allowance</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {safeRows.map((row, idx) => {
                        const itemKey = (row?.item || '').trim();
                        const isLastRow = idx === safeRows.length - 1;
                        const isWdvBfRow = itemKey === 'W.D.V. b/f';
                        const isBalancingAllRow = itemKey === 'Balancing allowance';
                        const isSalesProceedRow = itemKey === 'Less: Sales proceed';
                        const isTotalRow = idx === rowIndices.totalIdx;
                        const isAARow = itemKey === 'A.A.';
                        const isBalancingChargeRow = itemKey === 'Balancing charge';
                        const isWdvCfRow = itemKey === 'W.D.V. c/f';
                        const isLastBlankRow = idx === rowIndices.lastBlankIdx;

                        // 计算各列的值
                        let tenPoolValue = Number(row.tenPool) || 0;
                        let twentyPoolValue = Number(row.twentyPool) || 0;
                        let thirtyPoolValue = Number(row.thirtyPool) || 0;
                        let hpThirtyPoolValue = Number(row.hpThirtyPool) || 0;

                        if (isWdvBfRow) {
                            tenPoolValue = wdvPoolsState.tenPool;
                            twentyPoolValue = wdvPoolsState.twentyPool;
                            thirtyPoolValue = wdvPoolsState.thirtyPool;
                            hpThirtyPoolValue = wdvPoolsState.hpThirtyPool;
                        } else if (isSalesProceedRow) {
                            tenPoolValue = salesProceedPoolsState.tenPool;
                            twentyPoolValue = salesProceedPoolsState.twentyPool;
                            thirtyPoolValue = salesProceedPoolsState.thirtyPool;
                            hpThirtyPoolValue = salesProceedPoolsState.hpThirtyPool;
                        } else if (isTotalRow) {
                            // 1.1 Total行动态计算
                            tenPoolValue = totalRowPools.tenPool;
                            twentyPoolValue = totalRowPools.twentyPool;
                            thirtyPoolValue = totalRowPools.thirtyPool;
                            hpThirtyPoolValue = totalRowPools.hpThirtyPool;
                        } else if (isAARow) {
                            // 1.2 A.A.行动态计算
                            tenPoolValue = aaRowPools.tenPool;
                            twentyPoolValue = aaRowPools.twentyPool;
                            thirtyPoolValue = aaRowPools.thirtyPool;
                            hpThirtyPoolValue = aaRowPools.hpThirtyPool;
                        } else if (isBalancingChargeRow) {
                            // 1.3 Balancing charge行动态计算
                            tenPoolValue = balancingChargeRowPools.tenPool;
                            twentyPoolValue = balancingChargeRowPools.twentyPool;
                            thirtyPoolValue = balancingChargeRowPools.thirtyPool;
                            hpThirtyPoolValue = balancingChargeRowPools.hpThirtyPool;
                        } else if (isBalancingAllRow) {
                            // 1.4 Balancing allowance行用户输入
                            tenPoolValue = baPoolsState.tenPool;
                            twentyPoolValue = baPoolsState.twentyPool;
                            thirtyPoolValue = baPoolsState.thirtyPool;
                            hpThirtyPoolValue = baPoolsState.hpThirtyPool;
                        } else if (isWdvCfRow) {
                            // 1.5 W.D.V. c/f行动态计算
                            tenPoolValue = wdvCfRowPools.tenPool;
                            twentyPoolValue = wdvCfRowPools.twentyPool;
                            thirtyPoolValue = wdvCfRowPools.thirtyPool;
                            hpThirtyPoolValue = wdvCfRowPools.hpThirtyPool;
                        }

                        // 计算 Balancing charge, Balancing allowance, Allowance 列的值
                        let balancingChargeColValue = Number(row?.balancingCharge) || 0;
                        let balancingAllowanceColValue = Number(row?.balancingAllowance) || 0;
                        let allowanceColValue = Number(row?.allowance) || 0;

                        if (isBalancingChargeRow) {
                            // 1.3.2 Balancing charge行的Balancing charge列
                            balancingChargeColValue = balancingChargeRowPools.balancingCharge;
                        } else if (isBalancingAllRow) {
                            // 1.4 Balancing allowance行的Balancing allowance列
                            balancingAllowanceColValue = balancingAllowanceValue;
                        } else if (isLastBlankRow) {
                            // 1.6 最后一行汇总
                            balancingChargeColValue = lastRowSummary.balancingCharge;
                            balancingAllowanceColValue = lastRowSummary.balancingAllowance;
                        }

                        // 计算 A.A. 行的 Allowance 列值
                        if (isAARow) {
                            allowanceColValue = aaRowPools.tenPool + aaRowPools.twentyPool + aaRowPools.thirtyPool + aaRowPools.hpThirtyPool;
                        }

                        // 1.6.3 计算最后一行的 Allowance 列 (所有 Allowance 列值之和)
                        if (isLastBlankRow) {
                            let totalAllowance = 0;
                            rows.forEach((r, i) => {
                                const rItemKey = (r?.item || '').trim();
                                if (rItemKey === 'A.A.') {
                                    // totalAllowance += -(aaRowPools.tenPool + aaRowPools.twentyPool + aaRowPools.thirtyPool + aaRowPools.hpThirtyPool);
                                    totalAllowance += aaRowPools.tenPool + aaRowPools.twentyPool + aaRowPools.thirtyPool + aaRowPools.hpThirtyPool;
                                } else if (rItemKey === 'I.A. - 60%') {
                                    totalAllowance += Number(r?.allowance) || 0;
                                }
                            });
                            allowanceColValue = totalAllowance;
                        }

                        const shouldRenderAllowance = itemKey === 'A.A.' || itemKey === 'I.A. - 60%' || isLastBlankRow;
                        const shouldRenderBalancingAllowance = isBalancingChargeRow || isLastBlankRow;
                        const shouldRenderBalancingCharge = isBalancingAllRow || isLastBlankRow;

                        // 1.1 当 Total/A.A./Balancing charge/W.D.V. c/f 行的4个pool列值都为0时，不渲染该行
                        const allPoolsZero = tenPoolValue === 0 && twentyPoolValue === 0 && thirtyPoolValue === 0 && hpThirtyPoolValue === 0;
                        if ((isTotalRow || isAARow || isBalancingChargeRow || isWdvCfRow) && allPoolsZero) {
                            return null;
                        }

                        // Editable: W.D.V. b/f (MULTI later YOA locked), Balancing allowance.
                        // Less: Sales proceed is GOD-derived (read-only).
                        const wdvBfRowEditable = isWdvBfRow && wdvBfEditable;
                        const isEditableRow = wdvBfRowEditable || isBalancingAllRow;

                        // 1.2 Balancing allowance 行：当 total 行对应列为负数时，该列输入框不可编辑
                        const baTenEditable = isBalancingAllRow && totalRowPools.tenPool >= 0;
                        const baTwentyEditable = isBalancingAllRow && totalRowPools.twentyPool >= 0;
                        const baThirtyEditable = isBalancingAllRow && totalRowPools.thirtyPool >= 0;
                        const baHpThirtyEditable = isBalancingAllRow && totalRowPools.hpThirtyPool >= 0;

                        return (
                            <TableRow key={idx}>
                                <TableCell sx={{minWidth: 165}}>
                                    <Typography variant="body2" sx={{fontWeight: 'bold'}}>
                                        {row.item}
                                    </Typography>
                                </TableCell>
                                <TableCell align="center" sx={{minWidth: 120}}>
                                    {isLastBlankRow ? null : (
                                        <MoneyField
                                            value={tenPoolValue}
                                            editable={isBalancingAllRow ? baTenEditable : isEditableRow}
                                            tiny
                                            currencySymbol={currency || ''}
                                            showCents={false}
                                            align="center"
                                            onChangeNumberImmediate={(n) => isBalancingAllRow ? updatePools({tenPool: -Math.abs(n)}, 3) : updatePools({tenPool: Math.abs(n)})}
                                            onChangeNumber={(n) => isBalancingAllRow ? updatePools({tenPool: -Math.abs(n)}, 3) : updatePools({tenPool: Math.abs(n)})}
                                        />
                                    )}
                                </TableCell>
                                <TableCell align="center">
                                    {isLastBlankRow ? null : (
                                        <MoneyField
                                            value={twentyPoolValue}
                                            editable={isBalancingAllRow ? baTwentyEditable : isEditableRow}
                                            tiny
                                            currencySymbol={currency || ''}
                                            showCents={false}
                                            align="center"
                                            onChangeNumberImmediate={(n) => isBalancingAllRow ? updatePools({twentyPool: -Math.abs(n)}, 3) : updatePools({twentyPool: Math.abs(n)})}
                                            onChangeNumber={(n) => isBalancingAllRow ? updatePools({twentyPool: -Math.abs(n)}, 3) : updatePools({twentyPool: Math.abs(n)})}
                                        />
                                    )}
                                </TableCell>
                                <TableCell align="center">
                                    {isLastBlankRow ? null : (
                                        <MoneyField
                                            value={thirtyPoolValue}
                                            editable={isBalancingAllRow ? baThirtyEditable : (wdvBfRowEditable || isBalancingAllRow)}
                                            tiny
                                            currencySymbol={currency || ''}
                                            showCents={false}
                                            align="center"
                                            onChangeNumberImmediate={(n) => isBalancingAllRow ? updatePools({thirtyPool: -Math.abs(n)}, 3) : updatePools({thirtyPool: Math.abs(n)})}
                                            onChangeNumber={(n) => isBalancingAllRow ? updatePools({thirtyPool: -Math.abs(n)}, 3) : updatePools({thirtyPool: Math.abs(n)})}
                                        />
                                    )}
                                </TableCell>
                                <TableCell align="center">
                                    {isLastBlankRow ? null : (
                                        <MoneyField
                                            value={hpThirtyPoolValue}
                                            editable={isBalancingAllRow ? baHpThirtyEditable : (wdvBfRowEditable || isBalancingAllRow)}
                                            tiny
                                            currencySymbol={currency || ''}
                                            showCents={false}
                                            align="center"
                                            onChangeNumberImmediate={(n) => isBalancingAllRow ? updatePools({hpThirtyPool: -Math.abs(n)}, 3) : updatePools({hpThirtyPool: Math.abs(n)})}
                                            onChangeNumber={(n) => isBalancingAllRow ? updatePools({hpThirtyPool: -Math.abs(n)}, 3) : updatePools({hpThirtyPool: Math.abs(n)})}
                                        />
                                    )}
                                </TableCell>
                                <TableCell align="center">
                                    {shouldRenderBalancingAllowance ? (
                                        <MoneyField value={balancingChargeColValue} editable={false}
                                                    currencySymbol={currency || ''} showCents={false} align="center"/>
                                    ) : null}
                                </TableCell>
                                <TableCell align="center">
                                    {shouldRenderBalancingCharge ? (
                                        <MoneyField value={balancingAllowanceColValue} editable={false}
                                                    currencySymbol={currency || ''} showCents={false} align="center"/>
                                    ) : null}
                                </TableCell>
                                <TableCell align="center">
                                    {shouldRenderAllowance ? (
                                        <MoneyField value={allowanceColValue} editable={false}
                                                    currencySymbol={currency || ''}
                                                    showCents={false} align="center"/>
                                    ) : null}
                                </TableCell>
                            </TableRow>
                        );
                    })}

                    <TableRow>
                        <TableCell colSpan={8} sx={{py: 0.25, verticalAlign: 'middle'}}/>
                    </TableRow>
                    <TableRow>
                        <TableCell colSpan={8} sx={{py: 0.25, verticalAlign: 'middle'}}/>
                    </TableRow>
                </TableBody>
            </Table>
        </Box>
    );
}
